import CaptionService from "CaptionService";
import dialogService from "DialogService";
import { getInterfaceName } from "EntityExtensions";
import { WorkflowError, WorkflowErrorType } from "Errors";
import { NotificationType } from "NotificationType";
import type Rule from "Rule";
import RuleCommandResult from "RuleCommandResult";
import RuleService from "RuleService";
import { type Entity } from "breeze-client";
import ko from "knockout";

export type Command = {
  onClickAsync: (ruleContext?: object) => Promise<boolean>;
  isProcessing: ko.Observable<boolean>;
};

class WorkflowCommandRuleHandler {
  getClickHandler(entity: Entity, commandName: string): Command {
    const entityCasted = entity as unknown as Record<string, Command>;
    let command = entityCasted[commandName];

    if (!command) {
      const isProcessing = ko.observable(false);
      command = {
        onClickAsync: async (ruleContext): Promise<boolean> => {
          isProcessing(true);
          try {
            return await this.runCommandAsync(entity, commandName, ruleContext);
          } finally {
            isProcessing(false);
          }
        },
        isProcessing,
      };
      entityCasted[commandName] = command;
    }

    return command;
  }

  async runCommandAsync(entity: Entity, commandName: string, ruleContext?: object): Promise<boolean> {
    if (!entity) {
      /*! SuppressStringValidation only used in debugger log */
      throw new WorkflowError(WorkflowErrorType.EntityNotFound, "Entity was not found.");
    }

    const ruleService = await RuleService.loadAsync(getInterfaceName(entity));

    const rule = ruleService.commandRule(commandName, /*okIfNotFound:*/ true);
    if (!rule) {
      /*! SuppressStringValidation only used in debugger log */
      throw new WorkflowError(WorkflowErrorType.CommandNotFound, "Command " + commandName + " is not found.");
    }

    return await this.processRuleAsync(entity, rule, commandName, ruleContext);
  }

  private async processRuleAsync(
    entity: unknown,
    rule: Rule,
    commandName: string,
    ruleContext?: object,
  ): Promise<boolean> {
    const result = await rule.processAsync(entity, ruleContext);
    let message;
    if (!result.isSuccess) {
      message = CaptionService.getString(
        "6f406940-a81c-4fbc-9e3d-72db96b14aa5",
        'Cannot run command "{0}". This could be due to conditions, or security restrictions.',
        commandName,
      );
    } else if (result.value instanceof RuleCommandResult && !result.value.isSuccess()) {
      message = result.value.errorMessage;
    } else {
      return true;
    }

    await dialogService.showAsync(
      NotificationType.Error,
      message!,
      CaptionService.getString("54d69684-d59f-4f68-9574-5c78dde5b734", "Unable to Perform Operation"),
      [
        {
          caption: CaptionService.getString("4079e3c7-7d15-4679-9ee1-6efc4d65136b", "OK"),
          isPrimary: true,
          isDismiss: true,
        },
      ],
    );

    return false;
  }
}

export default new WorkflowCommandRuleHandler();
